// draggable.ts
import type { Directive, DirectiveBinding } from "vue";

interface DraggableOptions {
  // 初始位置，支持像素值或百分比
  initialX?: number | string;
  initialY?: number | string;
  // 是否在每次打开时重置位置
  resetOnOpen?: boolean;
}

interface DragElement extends HTMLElement {
  _dragHandler?: (e: MouseEvent) => void;
  _observer?: MutationObserver;
  _isInitialized?: boolean;
}

const parsePosition = (value: number | string, isX: boolean): number => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string" && value.endsWith("%")) {
    const percentage = parseFloat(value);
    const dimension = isX ? window.innerWidth : window.innerHeight;
    return (dimension * percentage) / 100;
  }

  return parseFloat(value) || 0;
};

export const vDraggable: Directive = {
  mounted(el: DragElement, binding: DirectiveBinding<DraggableOptions>) {
    const options = binding.value || {};
    const { initialX = 0, initialY = 0, resetOnOpen = false } = options;

    const initDrag = () => {
      // 从当前元素或全局查找 dialog
      let dialogHeaderEl = el.querySelector(
        ".el-dialog__header"
      ) as HTMLElement;
      let dialogEl = el.querySelector(".el-dialog") as HTMLElement;

      // 如果当前元素没找到，尝试从 body 查找（el-dialog 可能渲染到 body）
      if (!dialogHeaderEl || !dialogEl) {
        dialogHeaderEl = document.querySelector(
          ".el-dialog__header"
        ) as HTMLElement;
        dialogEl = document.querySelector(".el-dialog") as HTMLElement;
      }

      if (!dialogHeaderEl || !dialogEl) {
        return false;
      }

      // 设置 header 样式
      dialogHeaderEl.style.cursor = "move";
      dialogHeaderEl.style.userSelect = "none";

      // 设置初始偏移位置
      if (!el._isInitialized || resetOnOpen) {
        const x = parsePosition(initialX, true);
        const y = parsePosition(initialY, false);

        // 只有在设置了初始位置时才添加 transform
        if (x !== 0 || y !== 0) {
          dialogEl.style.transform = `translate(${x}px, ${y}px)`;
        }
        el._isInitialized = true;
      }

      const dragHandler = (e: MouseEvent) => {
        e.preventDefault();

        // 获取鼠标按下时的初始位置
        const startX = e.clientX;
        const startY = e.clientY;

        // 获取 dialog 当前的 transform 值
        const transform = window.getComputedStyle(dialogEl).transform;
        let translateX = 0;
        let translateY = 0;

        if (transform !== "none") {
          const matrix = transform.match(/matrix\((.+)\)/);
          if (matrix) {
            const values = matrix[1].split(", ");
            translateX = parseFloat(values[4]) || 0;
            translateY = parseFloat(values[5]) || 0;
          }
        }

        // 获取 dialog 的初始位置和尺寸
        const dialogRect = dialogEl.getBoundingClientRect();
        const dialogWidth = dialogRect.width;
        const dialogHeight = dialogRect.height;
        const dialogLeft = dialogRect.left;
        const dialogTop = dialogRect.top;

        // 至少保留多少像素可见
        const visiblePadding = 50;

        // 鼠标移动事件
        const moveHandler = (moveEvent: MouseEvent) => {
          // 计算鼠标移动的距离
          const deltaX = moveEvent.clientX - startX;
          const deltaY = moveEvent.clientY - startY;

          // 计算新的 translate 值（未限制）
          let newTranslateX = translateX + deltaX;
          let newTranslateY = translateY + deltaY;

          // 计算 dialog 的新位置
          const newLeft = dialogLeft + deltaX;
          const newTop = dialogTop + deltaY;
          const newRight = newLeft + dialogWidth;
          const newBottom = newTop + dialogHeight;

          // 边界限制 - 修正后的逻辑
          // 左边界：dialog 右边缘不能小于 visiblePadding
          if (newRight < visiblePadding) {
            newTranslateX =
              translateX + (visiblePadding - dialogWidth - dialogLeft);
          }
          // 右边界：dialog 左边缘不能大于 window.innerWidth - visiblePadding
          else if (newLeft > window.innerWidth - visiblePadding) {
            newTranslateX =
              translateX + (window.innerWidth - visiblePadding - dialogLeft);
          }

          // 上边界：不能超出顶部
          if (newTop < 0) {
            newTranslateY = translateY - dialogTop;
          }
          // 下边界：dialog 上边缘不能大于 window.innerHeight - visiblePadding
          else if (newTop > window.innerHeight - visiblePadding) {
            newTranslateY =
              translateY + (window.innerHeight - visiblePadding - dialogTop);
          }

          dialogEl.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px)`;
        };

        // 鼠标释放事件
        const upHandler = () => {
          document.removeEventListener("mousemove", moveHandler);
          document.removeEventListener("mouseup", upHandler);
        };

        document.addEventListener("mousemove", moveHandler);
        document.addEventListener("mouseup", upHandler);
      };

      // 保存事件处理器引用
      el._dragHandler = dragHandler;
      dialogHeaderEl.addEventListener("mousedown", dragHandler);
      return true;
    };

    // 尝试初始化
    setTimeout(() => {
      if (!initDrag()) {
        // 如果失败，使用 MutationObserver 等待 DOM 渲染
        const observer = new MutationObserver(() => {
          if (initDrag()) {
            observer.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        el._observer = observer;
      }
    }, 100);
  },

  updated(el: DragElement, binding: DirectiveBinding<DraggableOptions>) {
    // 当指令更新时（比如 dialog 重新打开），重新初始化
    const options = binding.value || {};
    const { resetOnOpen = false } = options;

    if (resetOnOpen) {
      el._isInitialized = false;
    }

    setTimeout(() => {
      const dialogHeaderEl = document.querySelector(
        ".el-dialog__header"
      ) as HTMLElement;
      const dialogEl = document.querySelector(".el-dialog") as HTMLElement;

      if (dialogHeaderEl && dialogEl && !el._isInitialized) {
        const { initialX = 0, initialY = 0 } = options;
        const x = parsePosition(initialX, true);
        const y = parsePosition(initialY, false);

        if (x !== 0 || y !== 0) {
          dialogEl.style.transform = `translate(${x}px, ${y}px)`;
        }
        el._isInitialized = true;
      }
    }, 100);
  },

  unmounted(el: DragElement) {
    const dialogHeaderEl = document.querySelector(
      ".el-dialog__header"
    ) as HTMLElement;
    if (dialogHeaderEl && el._dragHandler) {
      dialogHeaderEl.removeEventListener("mousedown", el._dragHandler);
      delete el._dragHandler;
    }

    if (el._observer) {
      el._observer.disconnect();
      delete el._observer;
    }

    delete el._isInitialized;
  }
};

// ============ 使用示例 ============
/*
// 1. 在 main.ts 中全局注册
import vDraggable from './directives/draggable'
app.directive('draggable', vDraggable)

// 2. 在组件中使用
<template>
  <div>
    <el-button @click="dialogVisible = true">打开对话框</el-button>
    
    <!-- 示例 1: 使用像素值设置初始位置 -->
    <div v-draggable="{ initialX: 100, initialY: 50 }">
      <el-dialog
        v-model="dialogVisible"
        title="可拖拽对话框"
        width="500px"
      >
        <span>向右偏移 100px，向下偏移 50px</span>
      </el-dialog>
    </div>

    <!-- 示例 2: 使用百分比设置初始位置 -->
    <div v-draggable="{ initialX: '10%', initialY: '5%' }">
      <el-dialog
        v-model="dialogVisible"
        title="可拖拽对话框"
        width="500px"
      >
        <span>向右偏移 10%，向下偏移 5%</span>
      </el-dialog>
    </div>

    <!-- 示例 3: 每次打开都重置到初始位置 -->
    <div v-draggable="{ initialX: 200, initialY: 100, resetOnOpen: true }">
      <el-dialog
        v-model="dialogVisible"
        title="可拖拽对话框"
        width="500px"
      >
        <span>每次打开都重置位置</span>
      </el-dialog>
    </div>

    <!-- 示例 4: 不设置初始位置（默认居中） -->
    <div v-draggable>
      <el-dialog
        v-model="dialogVisible"
        title="可拖拽对话框"
        width="500px"
      >
        <span>默认居中显示</span>
      </el-dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const dialogVisible = ref(false)
</script>
*/
